/*
 * Theme preference: light, dark, or follow the operating system.
 *
 * The palette itself lives in index.css and keys on a single `data-theme="dark"`
 * attribute (docs/adr/0014). This module owns the other half of that contract:
 * it resolves the three-state preference down to the two-state attribute, and
 * re-resolves it when the OS setting changes while "system" is selected.
 *
 * The same resolution runs inline in index.html before first paint, so the page
 * never flashes light before React mounts. Keep the two in sync — STORAGE_KEY
 * and the attribute values are the whole contract.
 */

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "bt-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** Reads the stored preference. Storage can throw (Safari private mode), hence the guard. */
export function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function prefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

export function resolvePreference(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") return prefersDark() ? "dark" : "light";
  return preference;
}

/** Stamps the resolved theme on <html>, which is what the palette in index.css selects on. */
export function applyPreference(preference: ThemePreference): ResolvedTheme {
  const resolved = resolvePreference(preference);
  document.documentElement.setAttribute("data-theme", resolved);
  return resolved;
}

const listeners = new Set<() => void>();
let preference: ThemePreference | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

function current(): ThemePreference {
  if (preference === null) preference = readPreference();
  return preference;
}

export function setPreference(next: ThemePreference): void {
  preference = next;
  try {
    // "system" is stored rather than cleared, so the choice survives a change of
    // OS setting instead of silently reverting to whatever the browser says.
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Storage unavailable: the choice still applies for this session.
  }
  applyPreference(next);
  notify();
}

/**
 * Starts the app on the stored preference and keeps "system" live.
 * Returns a teardown so tests (and StrictMode's double-mount) stay clean.
 */
export function initTheme(): () => void {
  applyPreference(current());
  if (typeof window.matchMedia !== "function") return () => {};
  const media = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (current() === "system") {
      applyPreference("system");
      notify();
    }
  };
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPreferenceSnapshot(): ThemePreference {
  return current();
}

/** Test seam: drops the memoised preference and every subscriber. */
export function resetThemeForTests(): void {
  preference = null;
  listeners.clear();
}
